
import React, { useState, useEffect, useMemo } from "react";
import { useTranslations } from "use-intl";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { InvoiceFormData, InvoiceUploadService } from "@/services/invoiceUploadService";
import { Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AccountApiClient } from "@/clients/accountApiClient";
import { getExpenseCategoryOptions, categoryRequires } from '@/constants/expenseCategories';
import { ParticipantsField } from '@/components/expenses/ParticipantsField';
import { sharedSubmitFields } from './submitFields';
import { createDocumentFormFields } from './documentFormFields';
import type { ValidationTranslator } from '@/components/upload/draftFormSchema';
import { userFacingErrorMessage } from '@/utils/userFacingError';

const PAYMENT_METHOD_VALUES = ['card', 'cash', 'bank_transfer', 'salary_adjustment'] as const;

/** Which label and help line the vendor field wears per document type; receipts and expenses read "merchant". */
const VENDOR_COPY = {
  payable: { label: 'vendor', help: 'vendorHelp' },
  receivable: { label: 'customer', help: 'customerHelp' },
} as const;

type Translator = ReturnType<typeof useTranslations>;

/** The form's schema, with its validation messages in the language the app shows. */
function createInvoiceFormSchema(t: Translator, validation: ValidationTranslator) {
  const participantSchema = z.object({
    source: z.enum(['employee', 'external']),
    employee_id: z.string().uuid().optional(),
    name: z.string().min(1, validation('nameRequired')),
    company: z.string().min(1, validation('companyRequired')),
  });

  return z.object({
    file: z.instanceof(File, { message: t('validation.fileRequired') }),
    ...createDocumentFormFields(validation), // vendor and dueDate stay optional for receipts
    documentType: z.enum(['payable', 'receivable', 'receipt', 'expense']).default('payable'),
    remittanceInformation: z.string().optional(), // Optional for receipts
    // Payable/receivable fields
    vendorAccountInput: z.string().optional(),
    sourceAccountId: z.string().optional(),
    payablePaymentDate: z.string().optional(),
    bankBic: z.string().optional(),
    bankName: z.string().optional(),
    // Receipt fields
    paymentMethod: z.enum(['card', 'cash', 'bank_transfer', 'salary_adjustment']).optional(),
    expenseCategory: z.string().optional(),
    participants: z.array(participantSchema).optional(),
  }).refine((data) => {
    // Vendor required for payable/receivable
    if ((data.documentType === 'payable' || data.documentType === 'receivable') && !data.vendor) {
      return false;
    }
    // Remittance information required for payable/receivable
    if ((data.documentType === 'payable' || data.documentType === 'receivable') && !data.remittanceInformation) {
      return false;
    }
    // Payment method required for receipts
    if (data.documentType === 'receipt' && !data.paymentMethod) {
      return false;
    }
    // Payment method required for expenses
    if (data.documentType === 'expense' && !data.paymentMethod) {
      return false;
    }
    // Transaction date required for receipts/expenses
    if ((data.documentType === 'receipt' || data.documentType === 'expense') && !data.transactionDate) {
      return false;
    }
    return true;
  }, {
    message: t('validation.requiredMissing'),
  }).superRefine((data, ctx) => {
    // FIN-1100: no date stands in for a missing one, so it is asked for by name, as in Needs Input.
    if ((data.documentType === 'payable' || data.documentType === 'receivable') && !data.dueDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['dueDate'],
        message: validation('dueDateRequired'),
      });
    }
    if (
      categoryRequires(data.expenseCategory, 'participants') &&
      (!data.participants || data.participants.length === 0)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['participants'],
        message: t('validation.participantRequired'),
      });
    }
  });
}

type InvoiceFormSchemaType = z.infer<ReturnType<typeof createInvoiceFormSchema>>;

interface InvoiceUploadFormProps {
  onSuccess?: () => void;
}

const InvoiceUploadForm: React.FC<InvoiceUploadFormProps> = ({ onSuccess }) => {
  const t = useTranslations('consultantInvoiceCreate.upload');
  const validation = useTranslations('consultantDraft.validation');
  const invoiceFormSchema = useMemo(
    () => createInvoiceFormSchema(t, validation),
    [t, validation],
  );
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [bankAccounts, setBankAccounts] = useState<Array<{ id: string; bankName: string; maskedNumber: string | null }>>([]);

  // Extraction workflow state
  const [extractionStatus, setExtractionStatus] = useState<'idle' | 'uploading' | 'extracting' | 'completed' | 'failed' | 'partial'>('idle');
  const [extractionProgress, setExtractionProgress] = useState<string>('');
  const [isDraftMode, setIsDraftMode] = useState<boolean>(false);
  const [draftInvoiceId, setDraftInvoiceId] = useState<string | null>(null);

  const uploadService = new InvoiceUploadService();
  
  const form = useForm<InvoiceFormSchemaType>({
    resolver: zodResolver(invoiceFormSchema),
    defaultValues: {
      vendor: "",
      amount: 0,
      currency: "USD",
      dueDate: "",
      transactionDate: "",
      documentType: 'payable',
      remittanceInformation: "",
      vendorAccountInput: "",
      sourceAccountId: "",
      payablePaymentDate: "",
      bankBic: "",
      bankName: "",
      paymentMethod: undefined,
      expenseCategory: undefined,
      participants: [],
    },
  });

  const documentTypeValue = form.watch("documentType");
  const dueDateValue = form.watch("dueDate");
  const vendorLabelKey = VENDOR_COPY[documentTypeValue as keyof typeof VENDOR_COPY]?.label ?? 'merchant';
  const vendorHelpKey = VENDOR_COPY[documentTypeValue as keyof typeof VENDOR_COPY]?.help ?? 'merchantHelp';

  // Fetch bank accounts when component mounts
  useEffect(() => {
    const fetchBankAccounts = async () => {
      try {
        const response = await AccountApiClient.getBankAccounts();
        setBankAccounts(response.accounts.map(account => ({
          id: account.id,
          bankName: account.bankName,
          maskedNumber: account.maskedNumber ?? null,
        })));
      } catch (error) {
        console.error("Failed to fetch bank accounts:", error);
        // Continue without bank accounts - it's optional
      }
    };

    fetchBankAccounts();
  }, []);

  // Auto-set payment date to due date when due date changes and it's a payable
  useEffect(() => {
    if (documentTypeValue === 'payable' && dueDateValue) {
      form.setValue("payablePaymentDate", dueDateValue);
    }
  }, [dueDateValue, documentTypeValue, form]);
  
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    console.log('handleFileChange called');
    console.log('File selected:', file);
    console.log('File type:', file?.type);
    console.log('File name:', file?.name);

    if (!file) {
      console.log('No file selected, returning early');
      return;
    }

    form.setValue("file", file);
    setSelectedFileName(file.name);

    console.log('Checking if file type supports extraction...');
    const supportedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    const isExtractionSupported = supportedTypes.includes(file.type);
    console.log('Extraction supported:', isExtractionSupported);

    // Start extraction workflow for PDFs and images
    if (isExtractionSupported) {
      console.log('File detected, starting extraction workflow...');
      setExtractionStatus('uploading');
      setExtractionProgress(file.type === 'application/pdf' ? t('progress.uploadingPdf') : t('progress.uploadingImage'));

      try {
        // 1. Upload file only
        const { filePath } = await uploadService.uploadFileOnly(file);

        // 2. Create draft with pending extraction status
        const pendingMetadata = {
          extractionStatus: 'pending' as const,
          confidence: 0,
          timestamp: new Date().toISOString(),
          extractionTimeMs: 0,
          fieldsExtracted: [],
          fieldsMissing: [],
          warnings: [],
        };

        let invoiceId: string;
        try {
          const draftResult = await uploadService.createDraftInvoice({
            extractedData: {},
            extractionMetadata: pendingMetadata,
            filePath,
            fileName: file.name,
            documentType: form.getValues("documentType"),
          });
          invoiceId = draftResult.invoiceId;
          console.log('Draft invoice created with ID:', invoiceId);
        } catch (draftError) {
          // Cleanup orphaned file if draft creation fails (through service layer)
          console.error('Draft creation failed, cleaning up uploaded file:', draftError);
          await uploadService.cleanupOrphanedFile(filePath);
          throw draftError;
        }

        // 3. Set draft mode immediately (before extraction)
        setIsDraftMode(true);
        setDraftInvoiceId(invoiceId);

        // 4. Attempt extraction
        setExtractionStatus('extracting');
        const extractionTime = file.type === 'application/pdf' ? t('progress.timePdf') : t('progress.timeImage');
        setExtractionProgress(t('progress.analyzing', { time: extractionTime }));

        try {
          const extractionResult = await uploadService.extractWithRetry(
            filePath,
            form.getValues("documentType")
          );

          console.log('Extraction result:', extractionResult);

          // 5a. Success: update draft with extraction results
          const updateResult = await uploadService.updateDraftExtraction({
            invoiceId,
            extractedData: extractionResult.data,
            extractionMetadata: extractionResult.metadata,
            vendorAccountFields: extractionResult.vendorAccountFields,
          });
          if (!updateResult.success) {
            console.error('Failed to persist extraction results for draft:', invoiceId);
          }

          // Pre-fill form with extracted data
          if (extractionResult.data.vendor) form.setValue("vendor", extractionResult.data.vendor);
          if (extractionResult.data.amount) form.setValue("amount", extractionResult.data.amount);
          if (extractionResult.data.currency) form.setValue("currency", extractionResult.data.currency);
          if (extractionResult.data.due_date) form.setValue("dueDate", extractionResult.data.due_date);
          if (extractionResult.data.transaction_date) form.setValue("transactionDate", extractionResult.data.transaction_date);
          if (extractionResult.data.remittance_information) form.setValue("remittanceInformation", extractionResult.data.remittance_information);
          if (extractionResult.vendorAccountFields) {
            // Only pre-fill the text input for account types that can be re-classified correctly.
            // account_number+clearing_number combos cannot round-trip through classifyVendorAccountInput,
            // so leave the input blank for those — the structured data is already on the draft.
            const fields = extractionResult.vendorAccountFields;
            const displayValue = fields.vendor_iban || fields.vendor_bankgiro || fields.vendor_plusgiro || '';
            if (displayValue) form.setValue("vendorAccountInput", displayValue);
          }
          if (extractionResult.data.bank_bic) form.setValue("bankBic", extractionResult.data.bank_bic);
          if (extractionResult.data.bank_name) form.setValue("bankName", extractionResult.data.bank_name);
          if (extractionResult.data.payment_method) form.setValue("paymentMethod", extractionResult.data.payment_method);
          if (extractionResult.data.expense_category) form.setValue("expenseCategory", extractionResult.data.expense_category);

          setExtractionStatus(extractionResult.metadata.extractionStatus === 'partial' ? 'partial' : 'completed');
          setExtractionProgress('');
          toast.success(t('toast.extracted'));

        } catch (extractionError) {
          // 5b. Failure: update draft with failed status
          console.error('Extraction failed:', extractionError);
          // Stored as a draft warning, which the draft view shows to the user.
          const errorMessage = userFacingErrorMessage(extractionError, t('toast.extractionWarning'));

          const failedMetadata = {
            extractionStatus: 'failed' as const,
            confidence: 0,
            timestamp: new Date().toISOString(),
            extractionTimeMs: 0,
            fieldsExtracted: [],
            fieldsMissing: ['all'],
            warnings: [errorMessage],
          };

          await uploadService.updateDraftExtraction({
            invoiceId,
            extractedData: {},
            extractionMetadata: failedMetadata,
            vendorAccountFields: null,
          }).catch(updateError => {
            console.error('Failed to update draft with error status:', updateError);
          });

          setExtractionStatus('failed');
          setExtractionProgress('');
          toast.error(t('toast.extractFailed'));
        }

      } catch (error) {
        console.error('Upload failed:', error);
        setExtractionStatus('failed');
        setExtractionProgress('');
        toast.error(t('toast.extractFailed'));
      }
    }
  };
  
  const onSubmit = async (data: InvoiceFormSchemaType) => {
    setIsUploading(true);

    try {
      if (isDraftMode && draftInvoiceId) {
        // Finalize existing draft
        console.log('Finalizing draft invoice:', draftInvoiceId);

        const result = await uploadService.finalizeDraftInvoice(draftInvoiceId, {
          vendor: data.vendor,
          amount: data.amount,
          currency: data.currency,
          dueDate: data.dueDate,
          transactionDate: data.transactionDate,
          documentType: data.documentType,
          remittanceInformation: data.remittanceInformation,
          ...sharedSubmitFields(data),
        });

        if (result.success) {
          form.reset();
          setSelectedFileName(null);
          setIsDraftMode(false);
          setDraftInvoiceId(null);
          setExtractionStatus('idle');
          setExtractionProgress('');
          if (onSuccess) onSuccess();
        } else if (result.alreadyFinalized) {
          // The service toasts every other refusal; this one it leaves to the caller.
          toast.info(t('toast.alreadyFinalized'));
        }
      } else {
        // Manual entry workflow (extraction failed or non-PDF)
        const formData: InvoiceFormData = {
          file: data.file,
          vendor: data.vendor || '',
          amount: data.amount,
          currency: data.currency,
          dueDate: data.dueDate || '',
          transactionDate: data.transactionDate || '',
          documentType: data.documentType,
          remittanceInformation: data.remittanceInformation || '',
          payablePaymentDate: data.payablePaymentDate,
          ...sharedSubmitFields(data),
        };

        const result = await uploadService.uploadInvoice(formData);

        if (result.success) {
          form.reset();
          setSelectedFileName(null);
          if (onSuccess) onSuccess();
        }
      }
    } catch (error) {
      console.error("Error processing invoice:", error);
      toast.error(t('toast.processFailed'));
    } finally {
      setIsUploading(false);
    }
  };
  
  const busy = extractionStatus === 'extracting' || isUploading;
  const submitIcon = busy
    ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
    : <Upload className="mr-2 h-4 w-4" />;
  let submitLabel = isDraftMode ? t('update') : t('submit');
  if (isUploading) submitLabel = isDraftMode ? t('updating') : t('uploading');
  if (extractionStatus === 'extracting') submitLabel = t('analyzing');

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('title')}</CardTitle>
        <CardDescription>
          {t('description')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* File Upload Field */}
            <FormField
              control={form.control}
              name="file"
              render={() => (
                <FormItem>
                  <FormLabel>{t('file')}</FormLabel>
                  <FormControl>
                    <div className="flex flex-col space-y-2">
                      <Input
                        id="invoice-file"
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handleFileChange}
                        className="cursor-pointer"
                      />
                      {selectedFileName && (
                        <p className="text-sm text-muted-foreground">
                          {t('selected', { name: selectedFileName })}
                        </p>
                      )}
                    </div>
                  </FormControl>
                  <FormDescription>
                    {t('fileHelp')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Document Type Selection */}
            <FormField
              control={form.control}
              name="documentType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('documentType')}</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      className="flex flex-col space-y-1"
                    >
                      <FormItem className="flex items-center space-x-3 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="payable" />
                        </FormControl>
                        <FormLabel className="font-normal">
                          {t('types.payable')}
                        </FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center space-x-3 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="receivable" />
                        </FormControl>
                        <FormLabel className="font-normal">
                          {t('types.receivable')}
                        </FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center space-x-3 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="receipt" />
                        </FormControl>
                        <FormLabel className="font-normal">
                          {t('types.receipt')}
                        </FormLabel>
                      </FormItem>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="expense" />
                        <span className="text-sm">
                          {t('types.expense')}
                        </span>
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormDescription>
                    {t('documentTypeHelp')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Vendor/Customer/Merchant Field */}
            <FormField
              control={form.control}
              name="vendor"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t(vendorLabelKey)}
                    {(documentTypeValue === 'receipt' || documentTypeValue === 'expense') && ` ${t('optional')}`}
                  </FormLabel>
                  <FormControl>
                    <Input placeholder={t('namePlaceholder')} {...field} />
                  </FormControl>
                  <FormDescription>
                    {t(vendorHelpKey)}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Remittance Information Field */}
            <FormField
              control={form.control}
              name="remittanceInformation"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('remittance')}</FormLabel>
                  <FormControl>
                    <Input placeholder={t('remittancePlaceholder')} {...field} />
                  </FormControl>
                  <FormDescription>
                    {t('remittanceHelp')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Amount and Currency Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('amount')}</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder={t('amountPlaceholder')}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="currency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('currency')}</FormLabel>
                    <FormControl>
                      <Input placeholder={t('currencyPlaceholder')} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Due Date Field — payable/receivable only */}
            {documentTypeValue !== 'receipt' && documentTypeValue !== 'expense' && (
              <FormField
                control={form.control}
                name="dueDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('dueDate')}</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Purchase Date Field — receipt/expense only */}
            {(documentTypeValue === 'receipt' || documentTypeValue === 'expense') && (
              <FormField
                control={form.control}
                name="transactionDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('purchaseDate')}</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Conditional Payable Fields */}
            {documentTypeValue === 'payable' && (
              <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
                <h3 className="text-sm font-medium">{t('paymentDetails')}</h3>
                
                {/* Vendor Account Field */}
                <FormField
                  control={form.control}
                  name="vendorAccountInput"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('vendorAccount')}</FormLabel>
                      <FormControl>
                        <Input placeholder={t('vendorAccountPlaceholder')} {...field} />
                      </FormControl>
                      <FormDescription>
                        {t('vendorAccountHelp')}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Bank BIC Field */}
                <FormField
                  control={form.control}
                  name="bankBic"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('bic')}</FormLabel>
                      <FormControl>
                        <Input placeholder={t('bicPlaceholder')} {...field} />
                      </FormControl>
                      <FormDescription>
                        {t('bicHelp')}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Bank Name Field */}
                <FormField
                  control={form.control}
                  name="bankName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('bankName')}</FormLabel>
                      <FormControl>
                        <Input placeholder={t('bankNamePlaceholder')} {...field} />
                      </FormControl>
                      <FormDescription>
                        {t('bankNameHelp')}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Source Account Field */}
                <FormField
                  control={form.control}
                  name="sourceAccountId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('sourceAccount')}</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={t('sourceAccountPlaceholder')} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {bankAccounts.length > 0 ? (
                            bankAccounts.map((account) => (
                              <SelectItem key={account.id} value={account.id}>
                                {`${account.bankName} - ${account.maskedNumber ?? t('noAccountNumber')}`}
                              </SelectItem>
                            ))
                          ) : (
                            <div className="px-2 py-1.5 text-sm text-muted-foreground">
                              {t('noBankAccounts')}
                            </div>
                          )}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        {t('sourceAccountHelp')}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Payment Date Field */}
                <FormField
                  control={form.control}
                  name="payablePaymentDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('paymentDate')}</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormDescription>
                        {t('paymentDateHelp')}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {/* Conditional Receipt/Expense Fields */}
            {(documentTypeValue === 'receipt' || documentTypeValue === 'expense') && (
              <div className="space-y-4 p-4 border rounded-lg bg-amber-50">
                <h3 className="text-sm font-medium">{t('receiptDetails')}</h3>

                {/* Payment Method Field */}
                <FormField
                  control={form.control}
                  name="paymentMethod"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('paymentMethod')}</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={t('paymentMethodPlaceholder')} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {PAYMENT_METHOD_VALUES.map((method) => (
                            <SelectItem key={method} value={method}>{t(`paymentMethods.${method}`)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        {t('paymentMethodHelp')}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Expense Category Field */}
                <FormField
                  control={form.control}
                  name="expenseCategory"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('expenseCategory')}</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={t('expenseCategoryPlaceholder')} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {getExpenseCategoryOptions().map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        {t('expenseCategoryHelp')}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <ParticipantsField
                  control={form.control}
                  setValue={form.setValue}
                  watch={form.watch}
                  categoryFieldName="expenseCategory"
                  participantsFieldName="participants"
                />

              </div>
            )}

            {/* Extraction Status Indicators */}
            {extractionStatus === 'extracting' && (
              <div className="flex items-center space-x-2 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                <span className="text-sm text-blue-700">{extractionProgress}</span>
              </div>
            )}

            {isDraftMode && extractionStatus === 'completed' && (
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-green-700">
                  {t('status.completed')}
                </p>
              </div>
            )}

            {isDraftMode && extractionStatus === 'partial' && (
              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-sm text-yellow-700">
                  {t('status.partial')}
                </p>
              </div>
            )}

            {extractionStatus === 'failed' && (
              <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                <p className="text-sm text-red-700">
                  {t('status.failed')}
                </p>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex gap-2">
              <Button
                type="submit"
                className="flex-1"
                disabled={isUploading || extractionStatus === 'extracting'}
              >
                {submitIcon}
                {submitLabel}
              </Button>

              {isDraftMode && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    form.reset();
                    setIsDraftMode(false);
                    setDraftInvoiceId(null);
                    setExtractionStatus('idle');
                    setSelectedFileName(null);
                    setExtractionProgress('');
                  }}
                >
                  {t('startOver')}
                </Button>
              )}
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default InvoiceUploadForm;
